import axios from "axios";
import {
  addQueuedRequest,
  cacheResponse,
  clearCachedResponses,
  getCachedResponse,
  getProjectedResponseData,
  getQueuedRequests,
  markQueuedRequestFailed,
  markQueuedRequestSyncing,
  markQueuedRequestWaitingForDependency,
  removeQueuedRequest,
  rewriteQueuedRequestReferences,
  saveOfflinePreparation,
} from "./database";
import { normalizeUrlPath } from "./policies";
import {
  canCacheOfflineRequest,
  canQueueOfflineMutation,
  getOfflineModulePolicy,
  isBlockedOfflineDelete,
} from "./modulePolicies";
import { restoreOfflineLogin, saveOfflineLogin } from "./offlineAuth";
import {
  createApiValidationError,
  validateApiMutation,
} from "./apiValidation";
import { createOptimisticQueuedItem } from "./optimisticData";
import {
  getCreationReferenceMappings,
  getUnresolvedOfflineReferences,
  replaceOfflineReferences,
} from "./offlineReferences";

const SYNC_LOCK_KEY = "evivesoma-offline-sync-lock";
const SYNC_LOCK_TTL = 60 * 1000;
let configured = false;
let syncing = false;
let preloading = null;

const isNetworkError = (error) =>
  !error.response && error.code !== "ERR_CANCELED" && error.code !== "ECONNABORTED";
const isLogout = (config) => normalizeUrlPath(config?.url) === "logout";
const isLogin = (config) => normalizeUrlPath(config?.url) === "login";

const getLoginCredentials = (config) => {
  try {
    return typeof config?.data === "string"
      ? JSON.parse(config.data)
      : config?.data || {};
  } catch {
    return {};
  }
};

const createRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
};

const createOfflineResponse = (config, data, status, extra = {}) => ({
  data,
  status,
  statusText: status === 202 ? "Queued for synchronization" : "Offline cache",
  headers: {},
  config,
  request: null,
  ...extra,
});

const createBlockedDeleteError = (config) => {
  const error = new Error(
    "Suppression indisponible hors connexion. Reconnectez-vous pour supprimer cette donnée."
  );
  error.code = "OFFLINE_DELETE_BLOCKED";
  error.config = config;
  error.offline = true;
  return error;
};

const createOfflineTransportError = (config) => {
  const error = new Error(
    "Cette donnée n'est pas disponible hors ligne. Reconnectez-vous puis préparez les données offline."
  );
  error.code = "OFFLINE_CACHE_MISS";
  error.config = config;
  error.offline = true;
  return error;
};

const isPermanentSyncError = (error) => {
  const status = error.response?.status;
  return status >= 400 && status < 500 && ![408, 409, 425, 429].includes(status);
};

const runWithFallbackLock = async (task) => {
  const owner = createRequestId();
  const now = Date.now();

  try {
    const current = JSON.parse(window.localStorage.getItem(SYNC_LOCK_KEY) || "null");
    if (current?.expiresAt > now) return false;
  } catch {
    // An invalid old lock can safely be replaced.
  }

  window.localStorage.setItem(
    SYNC_LOCK_KEY,
    JSON.stringify({ owner, expiresAt: now + SYNC_LOCK_TTL })
  );

  try {
    const acquired = JSON.parse(window.localStorage.getItem(SYNC_LOCK_KEY) || "null");
    if (acquired?.owner !== owner) return false;
    await task();
    return true;
  } finally {
    try {
      const current = JSON.parse(window.localStorage.getItem(SYNC_LOCK_KEY) || "null");
      if (current?.owner === owner) window.localStorage.removeItem(SYNC_LOCK_KEY);
    } catch {
      window.localStorage.removeItem(SYNC_LOCK_KEY);
    }
  }
};

const runWithSyncLock = async (task) => {
  if (navigator.locks?.request) {
    return navigator.locks.request(
      "evivesoma-offline-sync",
      { ifAvailable: true },
      async (lock) => {
        if (!lock) return false;
        await task();
        return true;
      }
    );
  }

  return runWithFallbackLock(task);
};

export const syncOfflineQueue = async (options = {}) => {
  const force = options?.force === true;
  if (syncing || !navigator.onLine) return false;

  return runWithSyncLock(async () => {
    if (syncing || !navigator.onLine) return;
    syncing = true;
    window.dispatchEvent(new CustomEvent("offline:sync-started"));

    try {
      const requests = await getQueuedRequests({ force });
      let synchronized = false;

      for (const queued of requests) {
        await markQueuedRequestSyncing(queued.id);
        const unresolved = getUnresolvedOfflineReferences({
          data: queued.data,
          params: queued.params,
          url: queued.url,
        });
        if (unresolved.length) {
          await markQueuedRequestWaitingForDependency(queued.id, unresolved);
          continue;
        }
        try {
          const response = await axios.request({
            method: queued.method,
            url: queued.url,
            baseURL: queued.baseURL,
            params: queued.params,
            data: queued.data,
            headers: {
              ...queued.headers,
              "X-Idempotency-Key": queued.clientRequestId,
            },
            offlineReplay: true,
          });
          if (String(response.data?.msg || "").includes("Error")) {
            const businessError = new Error(response.data.msg);
            businessError.response = { ...response, status: 422 };
            throw businessError;
          }

          const mappings = getCreationReferenceMappings(queued, response.data);
          await rewriteQueuedRequestReferences(queued.id, mappings);
          requests.forEach((pending) => {
            if (pending.id === queued.id) return;
            pending.data = replaceOfflineReferences(pending.data, mappings);
            pending.params = replaceOfflineReferences(pending.params, mappings);
            pending.url = replaceOfflineReferences(pending.url, mappings);
          });

          await removeQueuedRequest(queued.id);
          synchronized = true;
        } catch (error) {
          const permanent = isPermanentSyncError(error);
          await markQueuedRequestFailed(
            queued.id,
            error.response?.data?.msg ||
              error.message ||
              "Erreur de synchronisation",
            { blocked: permanent }
          );
          // Une erreur métier bloque seulement sa chaîne de dépendances. Une
          // panne réseau interrompt le lot, car les requêtes suivantes échoueraient aussi.
          if (!permanent) break;
        }
      }

      if (synchronized) {
        window.dispatchEvent(new CustomEvent('offline:data-refresh-needed'));
      }
    } finally {
      syncing = false;
      window.dispatchEvent(new CustomEvent("offline:queue-changed"));
      window.dispatchEvent(new CustomEvent("offline:sync-finished"));
    }
  });
};

const runOfflinePreload = async (options = {}) => {
  if (!navigator.onLine) {
    throw new Error("Une connexion est nécessaire pour préparer les données hors ligne.");
  }

  const policy = getOfflineModulePolicy();
  const resources = policy.preloadResources;
  if (!policy.enabled || resources.length === 0) {
    throw new Error("Le mode hors ligne n'est pas encore activé pour ce module.");
  }
  const failures = [];
  let completed = 0;

  window.dispatchEvent(
    new CustomEvent("offline:preload-started", {
      detail: { completed, total: resources.length },
    })
  );

  for (const resource of resources) {
    try {
      await axios.get(resource, { offlinePreload: true });
    } catch (error) {
      failures.push({
        resource,
        error: error.response?.data?.msg || error.message || "Téléchargement impossible",
      });
    } finally {
      completed += 1;
      const detail = { completed, total: resources.length, resource };
      options.onProgress?.(detail);
      window.dispatchEvent(new CustomEvent("offline:preload-progress", { detail }));
    }
  }

  const summary = {
    version: policy.preloadVersion,
    total: resources.length,
    succeeded: resources.length - failures.length,
    failures,
  };
  await saveOfflinePreparation(summary);
  window.dispatchEvent(new CustomEvent("offline:preload-finished", { detail: summary }));
  return summary;
};

export const preloadOfflineData = (options = {}) => {
  if (preloading) return preloading;

  preloading = runOfflinePreload(options).finally(() => {
    preloading = null;
  });
  return preloading;
};

export const isOfflinePreparationCurrent = (preparation) => {
  const policy = getOfflineModulePolicy();
  return (
    preparation?.version === policy.preloadVersion &&
    preparation?.total === policy.preloadResources.length &&
    preparation?.failures?.length === 0
  );
};

export const setupOfflineApi = () => {
  if (configured) return;
  configured = true;

  axios.interceptors.request.use(async (config) => {
    const validationErrors = validateApiMutation(config);
    if (Object.keys(validationErrors).length) {
      throw createApiValidationError(config, validationErrors);
    }

    const unresolvedReferences = getUnresolvedOfflineReferences({
      data: config.data,
      params: config.params,
      url: config.url,
    });
    if (
      !config.offlineReplay &&
      canQueueOfflineMutation(config) &&
      unresolvedReferences.length
    ) {
      config.offlineDependencyQueue = true;
      config.adapter = async () => {
        const queued = await addQueuedRequest(config);
        if (queued.coalesced) {
          const optimisticData = createOptimisticQueuedItem(queued);
          return createOfflineResponse(
            config,
            {
              msg:
                "Création locale mise à jour. Elle sera synchronisée avec vos corrections.",
              offline: true,
              queueId: queued.id,
              data: optimisticData,
            },
            202
          );
        }
        await markQueuedRequestWaitingForDependency(
          queued.id,
          unresolvedReferences
        );
        const optimisticData = createOptimisticQueuedItem({
          ...queued,
          status: "blocked",
          blockedReason: "dependency",
        });
        return createOfflineResponse(
          config,
          {
            msg:
              "Modification gardée localement : une donnée liée doit d’abord être synchronisée.",
            offline: true,
            waitingForDependency: true,
            queueId: queued.id,
            data: optimisticData,
          },
          202
        );
      };
    } else if (isLogin(config) && !navigator.onLine) {
      const credentials = getLoginCredentials(config);
      config.offlineAuth = true;
      config.adapter = async () => {
        const session = await restoreOfflineLogin({
          username: credentials.username,
          password: credentials.password,
        });
        return createOfflineResponse(config, session, 200, {
          offline: true,
          offlineAuth: true,
        });
      };
    } else if (!navigator.onLine) {
      if (canCacheOfflineRequest(config)) {
        const cached = await getCachedResponse(config, { allowExpired: true });
        if (cached) {
          config.offlineCache = true;
          config.adapter = async () => {
            const data = await getProjectedResponseData(config, cached.data);
            return createOfflineResponse(config, data, cached.status, {
              offline: true,
              cachedAt: cached.updatedAt,
              cachePolicy: cached.policy,
              stale: Boolean(cached.stale),
            });
          };
        } else {
          config.adapter = async () => Promise.reject(createOfflineTransportError(config));
        }
      } else {
        // Do not contact the API when the browser already knows it is offline.
        // The response interceptor queues supported writes and rejects the rest.
        config.adapter = async () => Promise.reject(createOfflineTransportError(config));
      }
    }

    if (canQueueOfflineMutation(config)) {
      config.headers = config.headers || {};
      config.headers["X-Idempotency-Key"] =
        config.headers["X-Idempotency-Key"] || createRequestId();
    }
    return config;
  });

  axios.interceptors.response.use(
    async (response) => {
      if (isLogin(response.config) && !response.config.offlineAuth) {
        const credentials = getLoginCredentials(response.config);
        try {
          await saveOfflineLogin({
            username: credentials.username,
            password: credentials.password,
            session: response.data,
          });
        } catch (error) {
          console.warn("Impossible de préparer la connexion hors ligne.", error);
        }
      } else if (isLogout(response.config)) {
        await clearCachedResponses();
      } else if (
        !response.config.offlineCache &&
        canCacheOfflineRequest(response.config) &&
        response.status >= 200 &&
        response.status < 300
      ) {
        try {
          await cacheResponse(response.config, response.data, response.status);
        } catch (error) {
          console.warn("Impossible de mettre la réponse en cache hors ligne.", error);
        }
      }
      if (canCacheOfflineRequest(response.config)) {
        response.data = await getProjectedResponseData(
          response.config,
          response.data
        );
      }
      return response;
    },
    async (error) => {
      const config = error.config;
      const canTryOfflineLogin =
        isLogin(config) &&
        !error.response &&
        error.code !== "ERR_CANCELED";
      if (!config || (!isNetworkError(error) && !canTryOfflineLogin)) {
        return Promise.reject(error);
      }

      if (isLogin(config)) {
        const credentials = getLoginCredentials(config);
        try {
          const session = await restoreOfflineLogin({
            username: credentials.username,
            password: credentials.password,
          });
          return createOfflineResponse(config, session, 200, {
            offline: true,
            offlineAuth: true,
          });
        } catch (offlineLoginError) {
          return Promise.reject(offlineLoginError);
        }
      }

      if (canCacheOfflineRequest(config)) {
        const cached = await getCachedResponse(config, { allowExpired: true });
        if (cached) {
          const data = await getProjectedResponseData(config, cached.data);
          return createOfflineResponse(config, data, cached.status, {
            offline: true,
            cachedAt: cached.updatedAt,
            cachePolicy: cached.policy,
            stale: Boolean(cached.stale),
          });
        }
      }

      if (isBlockedOfflineDelete(config)) {
        return Promise.reject(createBlockedDeleteError(config));
      }

      if (canQueueOfflineMutation(config)) {
        const queued = await addQueuedRequest(config);
        const optimisticData = createOptimisticQueuedItem(queued);
        return createOfflineResponse(
          config,
          {
            msg: queued.coalesced
              ? "Création locale mise à jour. Elle sera synchronisée avec vos corrections."
              : "Modification enregistrée hors ligne. Synchronisation automatique à venir.",
            offline: true,
            queueId: queued.id,
            data: optimisticData,
          },
          202
        );
      }

      return Promise.reject(error);
    }
  );

  window.addEventListener("online", syncOfflineQueue);
  if (navigator.onLine) syncOfflineQueue();
};
